import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { ActionDtoModel } from '@bo-client/activation-management/shared/models/actionModel';
import {
  ActionTypeEnum,
  AllAreaTypeEnum,
  AreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import {
  DistrictHeatCommissioningForm,
  GasCommissioningForm,
  PowerCommissioningForm,
  WaterCommissioningForm
} from '@bo-client/activation-management/shared/models/commissioning-form.types';
import { ConnectionObjectAdditionalInfoModel } from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { DeviceModel } from '@bo-client/activation-management/shared/models/deviceModel';
import { Meter } from '@bo-client/activation-management/shared/models/meter';
import { MeteringDevice } from '@bo-client/activation-management/shared/models/meteringDevice';
import { RegexToCheckEMail } from '@bo-client/shared/forms/forms.validators';
import { ContactDataDto } from '@bo-client/shared/models/contactDataDto';
import { DocumentDtoModel } from '@bo-client/shared/models/documentDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Injectable({
  providedIn: 'root'
})
export class CreateFormsCommissioningService {
  public form = new FormGroup({}, { updateOn: 'submit' });

  constructor(
    private fb: FormBuilder,
    private createFormsService: CreateFormsService,
    private dataShareService: DataShareService
  ) {
    // empty
  }

  getCommissioningFormByArea(
    area: AreaTypeEnum,
    commissioningRequestModelDto: CommissioningRequestDtoModel,
    conditionControlNames: { [key: string]: boolean | null }
  ):
    | PowerCommissioningForm
    | GasCommissioningForm
    | WaterCommissioningForm
    | DistrictHeatCommissioningForm
    | undefined {
    switch (area) {
      case AreaTypeEnum.POWER:
        return this.createCommissioningFormPower(commissioningRequestModelDto, conditionControlNames);
      case AreaTypeEnum.GAS:
        return this.createCommissioningFormGas(commissioningRequestModelDto, conditionControlNames);
      case AreaTypeEnum.WATER:
        return this.createCommissioningFormWater(commissioningRequestModelDto, conditionControlNames);
      case AreaTypeEnum.DISTRICTHEAT:
        return this.createCommissioningFormDistrictHeat(commissioningRequestModelDto, conditionControlNames);
    }
  }

  /**
   *  Behandelt die Gas-Spezifischen Felder für ein neues Inbetriebnahme Formular
   * @param data
   * @param conditionControlNames
   */
  createCommissioningFormGas(
    data: CommissioningRequestDtoModel,
    conditionControlNames: { [key: string]: boolean | null }
  ): GasCommissioningForm {
    const customerDataList: ContactDataDto[] = [data.customer];
    if (data.invoiceContact && data.invoiceContact.salutation) {
      customerDataList.push(data.invoiceContact);
    }
    if (!data.uploads) {
      data.uploads = [];
    }
    const idxSignedDoc = data.uploads.findIndex((ele) => ele.tag == 'bbs_approval');
    let itemSignedDoc: DocumentDtoModel = new DocumentDtoModel();
    if (idxSignedDoc != -1) {
      itemSignedDoc = data.uploads[idxSignedDoc];
    }
    if (data.actions.length == 0) {
      const newAction: ActionDtoModel = new ActionDtoModel();
      data.actions.push(newAction);
    }
    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }

    return this.fb.group({
      shortDescription: [data.shortDescription],
      installerFeedbackComment: [data.installerFeedbackComment],
      longDescription: [data.longDescription],
      externalId: [data.externalId],
      externalCustomerId: [data.externalCustomerId],
      gridConnectionPointId: [null],
      supplyAppointment: this.supplyAppointment(data),
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.createFormsService.connectionObjectAdditionalInfoForm(
        data.connectionObjectAdditionalInfo
      ),
      actions: this.fb.array(
        this.createFormsService
          .sortListById(data.actions)
          .map((data) => this.createActionsForm(data, AreaTypeEnum.GAS, 'MainForm'))
      ),
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      gasMoreInformation: this.gasMoreInformationForm(data.comment),
      // wenn installationHasChanged initial ist soll hier "true" genommen werden. sonst false
      installationHasChanged: [
        data.installationHasChanged != null
          ? data.installationHasChanged
          : this.dataShareService.tenantConfig.commissioning.gasConfig.installationHasChangedDefault,
        [Validators.required]
      ],
      chimneySweepMail: [
        data.chimneySweepMail,
        [this.chimneySweepMailRequiredValidator(), Validators.pattern(RegexToCheckEMail)]
      ],
      acceptConditions: this.gasConditions(conditionControlNames),
      signedDocument: this.createFormsService.createUploadForm(itemSignedDoc),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data)))
    }) as unknown as GasCommissioningForm;
  }

  createCommissioningFormPower(
    data: CommissioningRequestDtoModel,
    conditionsValues: any
  ): PowerCommissioningForm {
    const customerDataList: ContactDataDto[] = [data.customer];
    if (data.invoiceContact && data.invoiceContact.salutation) {
      customerDataList.push(data.invoiceContact);
    }
    if (data.actions.length == 0) {
      const newAction: ActionDtoModel = new ActionDtoModel();
      data.actions.push(newAction);
    }
    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }

    return this.fb.group({
      shortDescription: [data.shortDescription],
      installerFeedbackComment: [data.installerFeedbackComment],
      longDescription: [data.longDescription],
      externalId: [data.externalId],
      externalCustomerId: [data.externalCustomerId],
      meterOperatorBDEWCode: [data.meterOperatorBDEWCode],
      supplyAppointment: this.supplyAppointment(data),
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.createFormsService.connectionObjectAdditionalInfoForm(
        data.connectionObjectAdditionalInfo
      ),
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      transactionType: this.transactionType(data),
      actions: this.fb.array(
        this.createFormsService
          .sortListById(data.actions)
          .map((data) => this.createActionsForm(data, AreaTypeEnum.POWER, 'MainForm'))
      ),
      acceptConditions: this.powerConditions(conditionsValues),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data)))
    }) as unknown as PowerCommissioningForm;
  }

  createCommissioningFormDistrictHeat(
    data: CommissioningRequestDtoModel,
    conditionsValues: any
  ): DistrictHeatCommissioningForm {
    const customerDataList: ContactDataDto[] = [data.customer];
    if (data.invoiceContact && data.invoiceContact.salutation) {
      customerDataList.push(data.invoiceContact);
    }
    if (data.actions.length == 0) {
      const newAction: ActionDtoModel = new ActionDtoModel();
      data.actions.push(newAction);
    }
    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }

    return this.fb.group({
      shortDescription: [data.shortDescription],
      installerFeedbackComment: [data.installerFeedbackComment],
      longDescription: [data.longDescription],
      externalId: [data.externalId],
      externalCustomerId: [data.externalCustomerId],
      supplyAppointment: this.supplyAppointment(data),
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.createFormsService.connectionObjectAdditionalInfoForm(
        data.connectionObjectAdditionalInfo
      ),
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      transactionType: this.transactionType(data),
      actions: this.fb.array(
        this.createFormsService
          .sortListById(data.actions)
          .map((data) => this.createActionsForm(data, AreaTypeEnum.DISTRICTHEAT, 'MainForm'))
      ),
      acceptConditions: this.powerConditions(conditionsValues),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data)))
    }) as unknown as DistrictHeatCommissioningForm;
  }

  createCommissioningFormWater(
    data: CommissioningRequestDtoModel,
    conditionsValues: any
  ): WaterCommissioningForm {
    const customerDataList: ContactDataDto[] = [data.customer];
    if (data.invoiceContact && data.invoiceContact.salutation) {
      customerDataList.push(data.invoiceContact);
    }
    if (data.actions.length == 0) {
      const newAction: ActionDtoModel = new ActionDtoModel();
      data.actions.push(newAction);
    }
    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }

    return this.fb.group({
      shortDescription: [data.shortDescription],
      installerFeedbackComment: [data.installerFeedbackComment],
      longDescription: [data.longDescription],
      externalId: [data.externalId],
      externalCustomerId: [data.externalCustomerId],
      supplyAppointment: this.supplyAppointment(data),
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.createFormsService.connectionObjectAdditionalInfoForm(
        data.connectionObjectAdditionalInfo
      ),
      actions: this.fb.array(
        this.createFormsService
          .sortListById(data.actions)
          .map((data) => this.createActionsForm(data, AreaTypeEnum.WATER, 'MainForm'))
      ),
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      waterMoreInformation: this.waterMoreInformationForm(data.comment),
      acceptConditions: this.waterConditions(conditionsValues),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data)))
    }) as unknown as WaterCommissioningForm;
  }

  transactionType(data: CommissioningRequestDtoModel) {
    return this.fb.group({
      newDevice: [data.newDevice],
      eHeating: [data.eHeating],
      powerIncrease: [data.powerIncrease],
      devicesSplitting: [data.devicesSplitting],
      deviceMerging: [data.deviceMerging],
      apportionment: [data.apportionment],
      connectionUsageSetting: [data.connectionUsageSetting],
      changeMeasuringDevice: [data.changeMeasuringDevice],
      temporaryPower: [data.temporaryPower],
      generationPlant: [data.generationPlant],
      recommissioning: [data.recommissioning],
      meteringSystemRenewal: [data.meteringSystemRenewal],
      overheadToUndergroundCabling: [data.overheadToUndergroundCabling],
      charger: [data.charger],
      comment: [data.comment]
    });
  }

  createActionsForm(data: ActionDtoModel, areaType: AreaTypeEnum, actionType: string) {
    /**
     * See: https://thuegasmartservice.atlassian.net/wiki/spaces/SUS/pages/412745733/Custom+Validierung+Z+hlerst+nde
     */
    /**
     * MainForm // Installateur Aktion
     * CompletionForm // Abschluss Aktion
     */
    const validationActiveForCurrentForm =
      actionType == 'CompletionForm' &&
      areaType == AllAreaTypeEnum.POWER &&
      this.dataShareService.tenantConfig.commissioning.powerConfig.validateMeterInformation; // TODO: refactor this

    const hasDevices = data.devices?.length > 0;

    const isActionTypeWithNewMeter =
      actionType == ActionTypeEnum.commissioning || actionType == ActionTypeEnum.change;
    const isActionTypeWithExistingMeter = actionType == ActionTypeEnum.change;

    const newDeviceRequired = validationActiveForCurrentForm && hasDevices && isActionTypeWithNewMeter;
    const existingDeviceRequired =
      validationActiveForCurrentForm && hasDevices && isActionTypeWithExistingMeter;

    const isNoDecommissioning = actionType != ActionTypeEnum.decommissioning;

    const group = this.fb.group({
      id: [data.id],
      actionType: [data.actionType, Validators.required],
      deviceDesignation: [data.deviceDesignation],
      connectionPower: [data.connectionPower],
      continuousFlow: [data.continuousFlow],
      totalVolumeFlow: [
        data.totalVolumeFlow,
        areaType == AreaTypeEnum.WATER &&
        this.dataShareService.tenantConfig.commissioning.waterConfig.totalVolumeFlowRequired
          ? [Validators.required]
          : []
      ],
      totalVolumeFlowM3H: [
        data.totalVolumeFlowM3H,
        areaType == AreaTypeEnum.GAS &&
        this.dataShareService.tenantConfig.commissioning.gasConfig.totalVolumeFlowM3HRequired &&
        isNoDecommissioning
          ? [Validators.required]
          : []
      ],
      meterNumber: [data.meterNumber],
      facilityType: [data.facilityType],
      businessType: [data.businessType],
      flatDescription: [data.flatDescription],
      meterType: [data.meterType],
      newMeterType: [data.newMeterType],
      newRegulatorType: [data.newRegulatorType],
      deviceLocation: [data.deviceLocation],
      ownWaterSupply: [
        data.ownWaterSupply,
        areaType == AreaTypeEnum.WATER &&
        this.dataShareService.tenantConfig.commissioning.waterConfig.showOwnWaterSupply
          ? [Validators.required]
          : []
      ],
      isPressureBoostingSystem: [
        data.isPressureBoostingSystem,
        areaType == AreaTypeEnum.WATER &&
        this.dataShareService.tenantConfig.commissioning.waterConfig.showIsPressureBoostingSystem
          ? [Validators.required]
          : []
      ],
      isFireExtinguishingSystem: [
        data.isFireExtinguishingSystem,
        areaType == AreaTypeEnum.WATER &&
        this.dataShareService.tenantConfig.commissioning.waterConfig.showIsFireExtinguishingSystem
          ? [Validators.required]
          : []
      ],
      isDeviceReplacement: [
        data.isDeviceReplacement,
        areaType == AreaTypeEnum.GAS &&
        this.dataShareService.tenantConfig.commissioning.gasConfig.showDeviceReplacement
          ? [Validators.required]
          : []
      ],
      transformerNumber: [data.transformerNumber || ''],
      transformerSize: [data.transformerSize],
      busbarSize: [data.busbarSize || null],
      amperage: [data.amperage],
      operationType: [data.operationType],
      connectionFuseAmplitude: [data.connectionFuseAmplitude],
      yearlyConsumptionGreaterthreshold: [data.yearlyConsumptionGreaterthreshold],
      expectedAnnualConsumption: [data.expectedAnnualConsumption],
      expectedAnnualProduction: areaType === AreaTypeEnum.POWER ? [data.expectedAnnualProduction] : undefined,
      fixingType: [data.fixingType],
      comment: [data.comment],
      sealingNecessary: [data.sealingNecessary],
      provisionalMeter: [data.provisionalMeter],
      provisionalNeededUntil: [data.provisionalNeededUntil],
      tariffControl: [data.tariffControl],
      regulatorAlreadyExits: [data.regulatorAlreadyExits],
      existingHeatingType: [data.existingHeatingType],
      existingHeatingConstructionyear: [data.existingHeatingConstructionyear],
      existingHeatingPower: [data.existingHeatingPower],
      existingHeatingPowerwater: [data.existingHeatingPowerwater],
      existingMiscPower: [data.existingMiscPower],
      existingMiscPowerDesc: [data.existingMiscPowerDesc],
      heatingWaterPriority: [data.heatingWaterPriority],
      suppliesAdditionalBuildings: [data.suppliesAdditionalBuildings],
      additionalBuildingsDescription: [data.additionalBuildingsDescription],
      devices: this.fb.array(
        this.createFormsService.sortListById(data.devices).map((data) => this.devicesForm(data, areaType))
      ),
      newDevice: this.createMeteringDeviceForm(data.newDevice, newDeviceRequired, false),
      existingDevice: this.createMeteringDeviceForm(data.existingDevice, existingDeviceRequired, false),
      mobileConnectionStrength: data.mobileConnectionStrength ? [data.mobileConnectionStrength] : undefined,
      isFeederPresent: [data.isFeederPresent],
      feederType: [data.feederType],
      isConsumerPresent: [data.isConsumerPresent],
      consumerType: [data.consumerType],
      isAnnualConsumptionExceeded: [data.isAnnualConsumptionExceeded],
      erpOrderId: [data.erpOrderId],
      mandatoryInstallationCase: [data.mandatoryInstallationCase],
      isHomeOwner: [data.isHomeOwner],
      reasonForDisconnection: [data.reasonForDisconnection],
      inspection: this.fb.group({
        exceedingExpectedConsumption: [data.inspection?.exceedingExpectedConsumption ?? null],
        causesNoise: [data.inspection?.causesNoise ?? null],
        comment: [data.inspection?.comment ?? null],
        permissionToOpenDevice: [data.inspection?.permissionToOpenDevice ?? null],
        requestsAttendance: [data.inspection?.requestsAttendance ?? null]
      })
    });

    return group;
  }

  devicesForm(data: DeviceModel, areaType: AreaTypeEnum) {
    return this.fb.group({
      id: [data.id],
      deviceType: [data.deviceType],
      location: [data.location],
      deviceAlreadyRegistered: [data.deviceAlreadyRegistered],
      gridRegistrationReference: [data.gridRegistrationReference],
      gridRegistration: [data.gridRegistration],
      power: [data.power],
      load: [data.load],
      connectionValue: [data.connectionValue],
      manufacturer: [data.manufacturer],
      state: [data.state]
    });
  }

  private createMeteringDeviceForm(
    data: MeteringDevice,
    meterNumberRequired: boolean,
    meterValuesRequired: boolean
  ) {
    return this.fb.group({
      deviceType: [data?.deviceType, meterValuesRequired ? [Validators.required] : []],
      deviceNumber: [data?.deviceNumber, meterNumberRequired ? [Validators.required] : []],
      meterOne: this.createMeterForm(data?.meterOne, meterValuesRequired),
      meterTwo: this.createMeterForm(data?.meterTwo, false),
      meterThree: this.createMeterForm(data?.meterThree, false)
    });
  }

  private createMeterForm(data: Meter, required: boolean) {
    return this.fb.group({
      meterNo: [data?.meterNo, required ? [Validators.required] : []],
      obisCode: [data?.obisCode, required ? [Validators.required] : []],
      value: [data?.value, required ? [Validators.required] : []]
    });
  }

  supplyAppointment(data: {
    supplyAppointment: any;
    supplyAppointmentWishDate: any;
    supplyAppointmentWishPartday: any;
  }) {
    return this.fb.group({
      date: [data.supplyAppointment],
      time: [''],
      supplyAppointmentWishDate: [data.supplyAppointmentWishDate],
      supplyAppointmentWishPartday: [data.supplyAppointmentWishPartday]
    });
  }

  gasMoreInformationForm(data: string) {
    return this.fb.group({
      freeDescription: [data]
    });
  }

  waterMoreInformationForm(data: string) {
    return this.fb.group({
      freeDescription: [data]
    });
  }

  waterConditions(conditionsValues: any) {
    return this.fb.group({
      installationCorrectlyExecuted: [
        Object.keys(conditionsValues).length ? conditionsValues.installationCorrectlyExecuted : '',
        [Validators.required, this.createFormsService.mustTrue]
      ],
      ownerConsent: [
        Object.keys(conditionsValues).length ? conditionsValues.ownerConsent : '',
        [Validators.required, this.createFormsService.mustTrue]
      ]
    });
  }

  /**
   * Validates that if installationhasChanged is true, control sawell needs to have a value
   */
  chimneySweepMailRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // get to the root control
      let formRoot = control;
      while (formRoot.parent) {
        formRoot = formRoot.parent;
      }
      const installationHasChanged = formRoot?.get('installationHasChanged')?.value;
      return installationHasChanged && !control.value
        ? {
            required: true
          }
        : null;
    };
  }

  gasConditions(conditionControlNames: { [key: string]: boolean | null }) {
    return this.fb.group({
      installationCorrectlyExecuted: [
        Object.keys(conditionControlNames).length ? conditionControlNames.installationCorrectlyExecuted : '',
        [Validators.required, this.createFormsService.mustTrue]
      ],
      ownerConsent: [
        Object.keys(conditionControlNames).length ? conditionControlNames.ownerConsent : '',
        [Validators.required, this.createFormsService.mustTrue]
      ],
      chimneySweepConfirmation: [
        Object.keys(conditionControlNames).length ? conditionControlNames.chimneySweepConfirmation : '',
        [this.chimneySweepMailRequiredValidator()]
      ]
    });
  }

  powerConditions(conditionsValues: any) {
    return this.fb.group({
      installationCorrectlyExecuted: [
        Object.keys(conditionsValues).length ? conditionsValues.installationCorrectlyExecuted : '',
        [Validators.required, this.createFormsService.mustTrue]
      ],
      ownerConsent: [
        Object.keys(conditionsValues).length ? conditionsValues.ownerConsent : '',
        [Validators.required, this.createFormsService.mustTrue]
      ]
    });
  }

  loadDateField(date: Date, time: string): Date {
    const loadDate: Date = new Date(date);
    loadDate.setDate(loadDate.getDate());
    loadDate.setHours(+time.slice(0, 2));
    loadDate.setMinutes(+time.slice(3));
    return new Date(loadDate);
  }

  hasChangedSupplyDates(savedSupplyAppointment: Date, supplyAppointment: Date): boolean {
    let areEqual = true;
    const savedDate = new Date(savedSupplyAppointment);
    const newDate = new Date(supplyAppointment);

    if (
      savedDate.getFullYear() == newDate.getFullYear() &&
      savedDate.getMonth() == newDate.getMonth() &&
      savedDate.getDay() == newDate.getDay() &&
      savedDate.getUTCHours() == newDate.getUTCHours() &&
      savedDate.getUTCMinutes() == newDate.getUTCMinutes()
    ) {
      areEqual = false;
    }
    return areEqual;
  }

  greaterThanZero(control) {
    const enteredValue = control.value;
    return !(enteredValue > 0) ? { greaterThanZero: true } : null;
  }
}
